import type { Camera } from '@flobi/artwork-core/render';
import type { ScreenBox } from '../useEditorCanvas';
import type { PixelSize } from './contracts';

type Insets = { top: number; left: number; right?: number; bottom?: number; reserve?: number };

/** Only the room left by the open panels, toolbar and bottom dock. */
export function generationViewport(view: PixelSize, inset: Insets): ScreenBox {
  const left = Math.min(Math.max(0, inset.left), view.width);
  const top = Math.min(Math.max(0, inset.top + (inset.reserve || 0)), view.height);
  return { left, top, right: Math.max(left, view.width - (inset.right || 0)), bottom: Math.max(top, view.height - (inset.bottom || 0)) };
}

type WorldBox = { x: number; y: number; width: number; height: number };

/**
 * New results are loose canvas objects centred where the person is looking — beside, never on
 * top of, the boxes still generating (`taken`), so several runs can go at once.
 */
export function generationPlacement(size: PixelSize, view: PixelSize, camera: Camera, inset: Insets, taken: readonly WorldBox[] = []) {
  if (![size.width, size.height, camera.zoom].every(value => Number.isFinite(value) && value > 0)) throw Error('The generation size is invalid.');
  const room = generationViewport(view, inset);
  const ratio = size.width / size.height;
  const screenWidth = Math.max(1, Math.min(480, (room.right - room.left) * .6, (room.bottom - room.top) * .6 * ratio));
  const width = Math.max(1, Math.round(screenWidth / camera.zoom));
  const height = Math.max(1, Math.round(width / ratio));
  const x = camera.x + (room.left + room.right) / 2 / camera.zoom - width / 2;
  const y = camera.y + (room.top + room.bottom) / 2 / camera.zoom - height / 2;
  const gap = Math.round(Math.max(width, height) * 0.08);
  const blocking = (at: number) => taken.filter(box => at < box.x + box.width + gap && at + width + gap > box.x && y < box.y + box.height + gap && y + height + gap > box.y);
  // Step right past whatever is in the way until the spot is free; the canvas pans to it.
  let left = x;
  for (let step = 0, inWay = blocking(left); step < 64 && inWay.length; step++, inWay = blocking(left)) left = Math.max(...inWay.map(box => box.x + box.width + gap));
  return { width, height, transform: [1, 0, 0, 1, left, y] };
}
